import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getActionDeadline, getManualDeadline } from '@/lib/time';
import { generateContactKey } from '@/utils/contactKey';

interface ActionCenterCounts {
  overdueCount: number;
  dueTodayCount: number;
  totalPendingCount: number;
  loading: boolean;
}

// Database action type for this specific use case
interface DbAction {
  id: string;
  type: string;
  created_at: string;
  processed: boolean;
  action_data: any;
  campaign_id: string;
  prospect_id: string;
  call_id: string;
  campaigns?: {
    campaign_type: string;
  };
  // Optional properties that may be missing in some contexts
  confidence?: number;
  phrase?: string | null;
  mode?: string;
  campaign?: {
    name: string;
  };
}

export function useActionCenterCounts(): ActionCenterCounts {
  const { user } = useAuth();
  const [counts, setCounts] = useState<ActionCenterCounts>({
    overdueCount: 0,
    dueTodayCount: 0,
    totalPendingCount: 0,
    loading: true,
  });

  const fetchCounts = async () => {
    if (!user?.id) {
      setCounts((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      // Fetch unprocessed, active actions for the current user (exclude archived)
      // Add 30-day time bounds to prevent historical data scanning
      const thirtyDaysAgo = new Date(
        Date.now() - 30 * 24 * 60 * 60 * 1000
      ).toISOString();
      const { data: actions, error } = await supabase
        .from('call_actions')
        .select(
          `
          id,
          type,
          created_at,
          processed,
          action_data,
          campaign_id,
          prospect_id,
          call_id,
          status,
          campaigns(
            campaign_type
          )
        `
        )
        .eq('user_id', user.id)
        .eq('processed', false)
        .eq('status', 'active')
        .gte('created_at', thirtyDaysAgo)
        .limit(10); // EMERGENCY CIRCUIT BREAKER

      if (error) throw error;

      // Group actions by call_id to count unique calls (aligns with Action Center)
      const groupedByCall = groupActionsByCall(actions || []);
      const callIds = Object.keys(groupedByCall);

      let overdueCallCount = 0;
      let dueTodayCallCount = 0;
      const now = new Date();
      const todayStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

      // Check each call for overdue/due today status
      callIds.forEach((callId) => {
        const callActions = groupedByCall[callId];
        let callHasOverdue = false;
        let callHasDueToday = false;

        // Check if this call has any overdue or due today actions
        callActions.forEach((action) => {
          // Check if action has manual due date
          const manualDueAt =
            action.action_data &&
            typeof action.action_data === 'object' &&
            'due_at' in action.action_data
              ? (action.action_data.due_at as string)
              : null;

          let deadline;
          if (manualDueAt) {
            deadline = getManualDeadline(manualDueAt);
          } else {
            // Determine call mode from campaign type
            const callMode = action.campaigns?.campaign_type?.includes(
              'inbound'
            )
              ? 'inbound'
              : 'outbound';
            deadline = getActionDeadline(
              action.type,
              action.created_at,
              callMode
            );
          }

          if (deadline.isOverdue) {
            callHasOverdue = true;
          } else {
            // Check if due today
            const dueDate = manualDueAt
              ? new Date(manualDueAt)
              : new Date(
                  new Date(action.created_at).getTime() + 24 * 60 * 60 * 1000
                ); // Default 24hr deadline

            if (dueDate >= todayStart && dueDate < todayEnd) {
              callHasDueToday = true;
            }
          }
        });

        // Count call once per urgency level (prioritize overdue over due today)
        if (callHasOverdue) {
          overdueCallCount++;
        } else if (callHasDueToday) {
          dueTodayCallCount++;
        }
      });

      // FORENSIC INVESTIGATION: Capture dashboard count calls
      setCounts({
        overdueCount: overdueCallCount,
        dueTodayCount: dueTodayCallCount,
        totalPendingCount: callIds.length,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching action center counts:', error);
      setCounts((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchCounts();
  }, [user?.id]);

  // Real-time subscription removed to reduce API usage
  // Badge counts now update only on page load/refresh

  return counts;
}

// Pure function to group actions by call_id (aligns with Action Center)
function groupActionsByCall(actions: DbAction[]): Record<string, DbAction[]> {
  const grouped: Record<string, DbAction[]> = {};

  for (const action of actions) {
    // Group by call_id to match Action Center logic
    const callKey = action.call_id;

    if (!grouped[callKey]) {
      grouped[callKey] = [];
    }

    grouped[callKey].push(action);
  }

  // Sort actions within each group by created_at (newest first)
  Object.keys(grouped).forEach((key) => {
    grouped[key].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  });

  return grouped;
}
